(function () {
  'use strict';

  var CACHE_KEY = 'rhl-review-mix-v1';
  var CACHE_TTL = 10 * 60 * 1000;
  var MAP_VIEW = 'rhl-review-map';
  var MAX_PARALLEL = 4;

  function pageUrl(page, mode, grid) {
    var url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    if (mode === 'map') {
      url.searchParams.set('view', MAP_VIEW);
    } else {
      url.searchParams.delete('view');
      url.searchParams.set('section_id', grid.getAttribute('data-rhl-section-id') || 'main-collection');
    }
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  function cacheIdentity() {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('view');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url.pathname + url.search;
  }

  async function getPageMap(page) {
    var response = await fetch(pageUrl(page, 'map'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json,text/plain,*/*' }
    });
    if (!response.ok) throw new Error('Review map request failed: ' + response.status);
    var data = JSON.parse(await response.text());
    if (!data || !Array.isArray(data.items) || !Number.isFinite(Number(data.pages))) {
      throw new Error('Review map response was not valid JSON data.');
    }
    return data;
  }

  async function mapPool(values, worker) {
    var results = new Array(values.length);
    var cursor = 0;
    var runners = Math.min(MAX_PARALLEL, values.length);
    await Promise.all(Array.from({ length: runners }, async function () {
      while (cursor < values.length) {
        var index = cursor++;
        results[index] = await worker(values[index]);
      }
    }));
    return results;
  }

  async function loadProductMap() {
    var identity = cacheIdentity();
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.identity === identity && Date.now() - cached.savedAt < CACHE_TTL && Array.isArray(cached.items)) {
        return cached.items;
      }
    } catch (ignore) {}

    var first = await getPageMap(1);
    var pages = Math.max(1, Math.ceil(Number(first.pages) || 1));
    var rest = pages > 1 ? await mapPool(Array.from({ length: pages - 1 }, function (_, index) { return index + 2; }), getPageMap) : [];
    var dataPages = [first].concat(rest);
    var items = [];
    dataPages.forEach(function (data, pageIndex) {
      if (Number(data.page) !== pageIndex + 1 || !Array.isArray(data.items)) {
        throw new Error('Review map pages were incomplete or out of order.');
      }
      data.items.forEach(function (item) {
        if (item && typeof item.handle === 'string' && item.handle) {
          items.push({ handle: item.handle, reviewed: item.reviewed === true, sourceIndex: items.length });
        }
      });
    });
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ identity: identity, savedAt: Date.now(), items: items }));
    } catch (ignore) {}
    return items;
  }

  function mixProducts(items, pageSize) {
    var rated = [];
    var unrated = [];
    items.forEach(function (item) { (item.reviewed ? rated : unrated).push(item); });
    if (!rated.length || !unrated.length) return items.slice();

    var pageSizes = [];
    for (var offset = 0; offset < items.length; offset += pageSize) {
      pageSizes.push(Math.min(pageSize, items.length - offset));
    }
    var pageTargets = pageSizes.map(function (size) { return Math.ceil(size * 0.20); });
    var requiredReviews = pageTargets.reduce(function (sum, count) { return sum + count; }, 0);
    var canMeetEveryPage = rated.length >= requiredReviews;

    /* Meet the 20% floor on every page first. Spread the remaining reviewed
       inventory evenly; a 35% ceiling is possible only when collection coverage
       permits it and every product is still kept in the listing. */
    if (canMeetEveryPage) {
      var remainingReviews = rated.length - requiredReviews;
      while (remainingReviews > 0) {
        var bestPage = -1;
        var bestRatio = Infinity;
        for (var p = 0; p < pageSizes.length; p++) {
          if (pageTargets[p] >= pageSizes[p]) continue;
          var ratio = pageTargets[p] / pageSizes[p];
          if (ratio < bestRatio) { bestRatio = ratio; bestPage = p; }
        }
        if (bestPage < 0) break;
        pageTargets[bestPage]++;
        remainingReviews--;
      }
    } else {
      /* Best effort for a collection/filter with too few reviewed products. */
      var rate = rated.length / items.length;
      var targetRate = Math.max(0.20, rate);
      var fallbackRatedAt = 0;
      var fallbackUnratedAt = 0;
      var fallback = [];
      while (fallbackRatedAt < rated.length || fallbackUnratedAt < unrated.length) {
        var useRated = false;
        if (fallbackRatedAt >= rated.length) useRated = false;
        else if (fallbackUnratedAt >= unrated.length) useRated = true;
        else if (fallback.length === 0) useRated = true;
        else useRated = fallbackRatedAt / fallback.length < targetRate;
        fallback.push(useRated ? rated[fallbackRatedAt++] : unrated[fallbackUnratedAt++]);
      }
      return fallback;
    }

    var ratedAt = 0;
    var unratedAt = 0;
    var output = [];

    pageSizes.forEach(function (size, pageIndex) {
      var targetRated = pageTargets[pageIndex];
      var targetUnrated = size - targetRated;
      var pageRated = 0;
      var pageUnrated = 0;
      for (var position = 0; position < size; position++) {
        var chooseRated = false;
        if (pageRated >= targetRated) chooseRated = false;
        else if (pageUnrated >= targetUnrated) chooseRated = true;
        else if (position === 0) chooseRated = true;
        else chooseRated = pageRated / position < targetRated / size;

        if (chooseRated) { output.push(rated[ratedAt++]); pageRated++; }
        else { output.push(unrated[unratedAt++]); pageUnrated++; }
      }
    });
    return output;
  }

  function cardHandle(card) {
    var link = card.querySelector('a[data-pr-url][href], a.hdt-card-product__title[href], a[href*="/products/"]');
    if (!link) return '';
    try {
      var path = new URL(link.getAttribute('href'), window.location.origin).pathname;
      var match = path.match(/\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (ignore) {
      return '';
    }
  }

  function cardsIn(grid) {
    return Array.from(grid.children).filter(function (child) { return !!cardHandle(child); });
  }

  function cardsFromSection(html, expectedSectionId) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var grids = Array.from(parsed.querySelectorAll('[data-rhl-review-mix-grid]'));
    var source = grids.find(function (grid) {
      return grid.getAttribute('data-rhl-section-id') === expectedSectionId;
    }) || grids[0];
    if (!source) throw new Error('Could not find the collection grid in the requested page.');
    var result = new Map();
    cardsIn(source).forEach(function (card) {
      var handle = cardHandle(card);
      if (handle && !result.has(handle)) result.set(handle, card);
    });
    return result;
  }

  async function getSourcePage(page, grid, sectionId) {
    var response = await fetch(pageUrl(page, 'section', grid), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html,*/*', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Collection page request failed: ' + response.status);
    return cardsFromSection(await response.text(), sectionId);
  }

  async function reorderGrid(grid) {
    if (grid.getAttribute('data-rhl-review-mix-state')) return;
    var pageSize = Number(grid.getAttribute('data-rhl-page-size'));
    var sectionId = grid.getAttribute('data-rhl-section-id') || 'main-collection';
    var currentPage = Number(new URL(window.location.href).searchParams.get('page')) || 1;
    if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 250) return;

    grid.setAttribute('data-rhl-review-mix-state', 'loading');
    try {
      var items = await loadProductMap();
      if (items.length < 2) {
        grid.setAttribute('data-rhl-review-mix-state', 'done');
        return;
      }
      var ordered = mixProducts(items, pageSize);
      var start = (currentPage - 1) * pageSize;
      var pageItems = ordered.slice(start, start + pageSize);
      if (!pageItems.length) throw new Error('The requested collection page is empty.');

      var currentCards = new Map();
      cardsIn(grid).forEach(function (card) {
        var handle = cardHandle(card);
        if (handle && !currentCards.has(handle)) currentCards.set(handle, card);
      });

      var sourcePages = Array.from(new Set(pageItems.map(function (item) {
        return Math.floor(item.sourceIndex / pageSize) + 1;
      }).filter(function (page) { return page !== currentPage; })));
      var fetchedPages = sourcePages.length ? await mapPool(sourcePages, function (page) {
        return getSourcePage(page, grid, sectionId);
      }) : [];
      var cardsByHandle = new Map(currentCards);
      fetchedPages.forEach(function (pageCards) {
        pageCards.forEach(function (card, handle) {
          if (!cardsByHandle.has(handle)) cardsByHandle.set(handle, card);
        });
      });

      var finalCards = pageItems.map(function (item) {
        var card = cardsByHandle.get(item.handle);
        if (!card) throw new Error('A product card could not be fetched: ' + item.handle);
        return card.ownerDocument === document ? card : document.importNode(card, true);
      });
      /* Imported cards arrive after the theme's one-time scroll reveal.
         Give every final card a visible base state before mounting it; otherwise
         the theme's opacity:0 rule hides fresh cards indefinitely. */
      finalCards.forEach(function (card) {
        card.style.opacity = '1';
        card.style.transform = 'translateY(0px)';
      });
      grid.replaceChildren.apply(grid, finalCards);
      grid.setAttribute('data-rhl-review-mix-state', 'done');
      grid.dispatchEvent(new CustomEvent('rhl:review-mix:complete', { bubbles: true }));
    } catch (error) {
      grid.setAttribute('data-rhl-review-mix-state', 'failed');
      if (window.console && console.warn) console.warn('[RHL review mix] Kept original collection order.', error);
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var grids = [];
    if (scope.matches && scope.matches('[data-rhl-review-mix-grid]')) grids.push(scope);
    grids = grids.concat(Array.from(scope.querySelectorAll('[data-rhl-review-mix-grid]')));
    grids.forEach(reorderGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
